/**
 * Recover from corrupted Firestore IndexedDB persistence (dev HMR / hard refresh races).
 * Symptom: Cannot read properties of null (reading 'ue') in canonifyTarget,
 * or FIRESTORE INTERNAL ASSERTION FAILED: Unexpected state.
 */

export const FIRESTORE_CACHE_RECOVERY_KEY = "tra_firestore_cache_recovery";

/**
 * @param {unknown} err
 */
export function isFirestoreInternalError(err) {
  if (!err) return false;
  const msg = String(
    typeof err === "string"
      ? err
      : err.message || err.reason || err.toString?.() || ""
  );
  if (/INTERNAL ASSERTION FAILED/i.test(msg)) return true;
  if (/Unexpected state/i.test(msg) && /FIRESTORE/i.test(msg)) return true;
  if (/Cannot read properties of null \(reading ['"]?ue['"]?\)/i.test(msg)) return true;
  if (/canonifyTarget/i.test(msg)) return true;
  const stack = String(err.stack || "");
  if (/__PRIVATE_canonifyTarget/i.test(stack) && /null/i.test(msg)) return true;
  return false;
}

/**
 * Best-effort delete of Firebase / Firestore IndexedDB databases, then reload once.
 * @returns {Promise<boolean>} true if a reload was scheduled
 */
export async function recoverFirestoreLocalCacheAndReload() {
  if (typeof window === "undefined") return false;
  try {
    if (sessionStorage.getItem(FIRESTORE_CACHE_RECOVERY_KEY)) {
      sessionStorage.removeItem(FIRESTORE_CACHE_RECOVERY_KEY);
      return false;
    }
    sessionStorage.setItem(FIRESTORE_CACHE_RECOVERY_KEY, "1");
  } catch {
    return false;
  }

  try {
    if (typeof indexedDB !== "undefined" && typeof indexedDB.databases === "function") {
      const dbs = await indexedDB.databases();
      await Promise.all(
        (dbs || [])
          .filter((d) => {
            const name = String(d?.name || "");
            return /firestore|firebase/i.test(name);
          })
          .map(
            (d) =>
              new Promise((resolve) => {
                const req = indexedDB.deleteDatabase(d.name);
                req.onsuccess = () => resolve();
                req.onerror = () => resolve();
                req.onblocked = () => resolve();
              })
          )
      );
    }
  } catch {
    /* best-effort */
  }

  window.location.reload();
  return true;
}

/**
 * Catch unhandled Firestore persistence crashes (esp. CRA error overlay on refresh).
 */
export function installFirestorePersistenceRecovery() {
  if (typeof window === "undefined") return;
  if (window.__traFirestorePersistenceRecoveryInstalled) return;
  window.__traFirestorePersistenceRecoveryInstalled = true;

  const onProblem = (event) => {
    const err = event?.reason || event?.error || event?.message;
    if (!isFirestoreInternalError(err)) return;
    if (typeof event.preventDefault === "function") event.preventDefault();
    void recoverFirestoreLocalCacheAndReload();
  };

  window.addEventListener("unhandledrejection", onProblem);
  window.addEventListener("error", onProblem);
}
